import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../auth/firebaseConfig';
import { useAuth } from '../auth/AuthContext';
import { adminEnvironment } from '../config/environment';
import logoLight from '../../../tools/src/assets/Logo_Semplora_Light_Grey.png';
import '../styles/login.scss';

/**
 * Login dedicato all'admin. Email/password + Google sign-in.
 *
 * Differenze rispetto al login user:
 * - Niente "ricordami" su questo device (la persistenza e' SESSION, vedi
 *   firebaseConfig.ts → setPersistence(browserSessionPersistence)).
 * - Google sign-in con prompt=select_account forzato.
 * - Pannello petrolio Semplora con l'ambiente in evidenza: si capisce prima
 *   di entrare su quali dati si sta per lavorare.
 */
const friendlyError = (err: unknown, fallback: string): string => {
    const code = (err as { code?: string })?.code ?? '';
    if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found'))
        return 'Email o password non corretti.';
    if (code.includes('too-many-requests')) return 'Troppi tentativi. Riprova tra qualche minuto.';
    if (code.includes('popup-closed')) return 'Finestra di accesso chiusa prima di completare il login.';
    if (code.includes('network')) return 'Connessione non disponibile. Controlla la rete e riprova.';
    return err instanceof Error ? err.message : fallback;
};

const LoginPage: React.FC = () => {
    const { status } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Se siamo gia' admin loggati, esci dal login.
    React.useEffect(() => {
        if (status === 'admin') navigate('/dashboard', { replace: true });
    }, [status, navigate]);

    const onEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            await signInWithEmailAndPassword(auth, email.trim(), password);
            // Il verifyAdmin di AuthContext fara' il redirect in base al ruolo.
        } catch (err: unknown) {
            setError(friendlyError(err, 'Errore autenticazione'));
        } finally {
            setSubmitting(false);
        }
    };

    const onGoogleLogin = async () => {
        setError(null);
        setSubmitting(true);
        try {
            await signInWithPopup(auth, googleProvider);
        } catch (err: unknown) {
            setError(friendlyError(err, 'Errore Google sign-in'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-login">
            <section className="admin-login__brand" aria-hidden="true">
                <img src={logoLight} alt="" className="admin-login__logo" />
                <div className="admin-login__claim">
                    <span className="admin-login__kicker">Area amministrativa</span>
                    <h1>Governa la piattaforma <span>con chiarezza.</span></h1>
                    <p>
                        Catalogo dei tool, accessi degli utenti, qualità e code di lavoro
                        in un unico punto di controllo.
                    </p>
                </div>
                <ul className="admin-login__points">
                    <li><i className="bi bi-shield-lock" /> Ruolo verificato sul server ad ogni richiesta</li>
                    <li><i className="bi bi-journal-check" /> Ogni modifica finisce nell’audit log</li>
                    <li><i className="bi bi-hourglass-split" /> Sessione valida finché il browser resta aperto</li>
                </ul>
                <div className="admin-login__grid" />
            </section>

            <section className="admin-login__panel">
                <div className="admin-login__card">
                    <div className="admin-login__head">
                        <span className={`env-pill is-${adminEnvironment.kind}`}>
                            <i aria-hidden="true" />Ambiente {adminEnvironment.label}
                        </span>
                        <h2>Accedi a Semplora Admin</h2>
                        <p>Accesso riservato agli amministratori.</p>
                    </div>

                    {error && (
                        <div className="alert alert-danger py-2 d-flex gap-2 align-items-start" role="alert">
                            <i className="bi bi-exclamation-triangle mt-1" />
                            <span>{error}</span>
                        </div>
                    )}

                    <button
                        type="button"
                        disabled={submitting}
                        onClick={onGoogleLogin}
                        className="btn btn-outline-secondary w-100 admin-login__google">
                        <i className="bi bi-google me-2" />
                        Continua con Google
                    </button>

                    <div className="admin-login__divider"><span>oppure con email</span></div>

                    <form onSubmit={onEmailLogin} noValidate>
                        <div className="mb-3">
                            <label className="form-label" htmlFor="admin-email">Email</label>
                            <input
                                id="admin-email"
                                type="email"
                                className="form-control"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                disabled={submitting}
                                required
                                autoFocus
                                autoComplete="username"
                                placeholder="nome@azienda.it"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="form-label" htmlFor="admin-password">Password</label>
                            <div className="input-group">
                                <input
                                    id="admin-password"
                                    type={showPassword ? 'text' : 'password'}
                                    className="form-control"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    disabled={submitting}
                                    required
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowPassword(s => !s)}
                                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}>
                                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                                </button>
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={submitting || !email || !password}
                            className="btn btn-primary w-100 admin-login__submit">
                            {submitting
                                ? <><span className="spinner-border spinner-border-sm me-2" /> Accesso…</>
                                : <>Accedi <i className="bi bi-arrow-right ms-2" /></>}
                        </button>
                    </form>
                </div>
                <p className="admin-login__foot">Semplora · Work made simple.</p>
            </section>
        </div>
    );
};

export default LoginPage;
